// @ts-nocheck
import React, { useEffect, useMemo, useState } from "react";
import SoftBox from "../../../components/common/softBox";
import SoftTypography from "../../../components/common/softTypography";
import SoftInput from "../../../components/common/softInput";
import SoftButton from "../../../components/common/softButton";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { getRoleByIdData, updateRoleData } from "../../../features/role";
import DashboardLayout from "../../../components/layoutContainers/dashboardLayout";
import DashboardNavbar from "../../../components/navbars/dashboardNavbar";
import {
  Card,
  Checkbox,
  FormControl,
  MenuItem,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from "@mui/material";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import { getPermissionData } from "../../../features/permission";

const PERMISSION_GROUPS = [
  { module: "User", slugs: ["create-user", "view-user", "update-user", "delete-user"] },
  { module: "Role", slugs: ["create-role", "update-role", "delete-role", "view-role", "assign-role"] },
  { module: "Role Permission", slugs: ["view-role-permission", "assign-role-permission"] },
  { module: "Permission", slugs: ["create-permission", "view-permission", "update-permission", "delete-permission"] },
  { module: "Product", slugs: ["create-product", "update-product", "delete-product", "view-product"] },
  { module: "Cms", slugs: ["create-cms", "update-cms", "delete-cms", "view-cms"] },
  { module: "Service", slugs: ["create-service", "update-service", "delete-service", "view-service"] },
  { module: "Publication", slugs: ["create-publication", "update-publication", "delete-publication", "view-publication"] },
  { module: "Promotion", slugs: ["create-promotion", "update-promotion", "delete-promotion", "view-promotion"] },
  { module: "Contact Us", slugs: ["create-contact-us", "delete-contact-us", "view-contact-us", "reply-contact-us"] },
  { module: "Credit Segment Code", slugs: ["create-credit-segment-code", "update-credit-segment-code", "delete-credit-segment-code", "view-credit-segment-code"] },
  { module: "Order", slugs: ["create-order", "update-order", "delete-order", "view-order"] },
  { module: "Order Pdf", slugs: ["view-order-pdf"] },
  { module: "Status", slugs: ["order-status"] },
  { module: "Credit Limit", slugs: ["create-credit-limit", "update-credit-limit", "delete-credit-limit", "view-credit-limit"] },
  { module: "Limit Status", slugs: ["credit-limit-status"] },
  { module: "Notification", slugs: ["create-notification", "read-notification", "view-notification"] },
  { module: "Order Review", slugs: ["create-order-review", "update-order-review", "delete-order-review", "view-order-review"] },
  { module: "Message", slugs: ["send-message", "view-message"] },
  { module: "Return Sale Order", slugs: ["create-return-sale-order", "update-return-sale-order", "delete-return-sale-order", "view-return-sale-order"] },
  { module: "Return Sale Order Status", slugs: ["update-return-sale-order-status"] },
  { module: "Dashboard", slugs: ["view-dashboard"] },
  { module: "Article", slugs: ["create-article", "view-article", "update-article", "delete-article"] },
  { module: "Configuration", slugs: ["view-configuration", "update-configuration"] },
  { module: "Cms Privacy Policy", slugs: ["view-cms-privacy-policy", "update-cms-privacy-policy"] },
  { module: "Cms Terms Of Service", slugs: ["view-cms-terms-of-service", "update-cms-terms-of-service"] },
  { module: "Contact Info", slugs: ["view-contact-info", "update-contact-info"] },
  { module: "Localization", slugs: ["view-localization", "update-localization"] },
  { module: "Approve", slugs: ["legal-approve"] },
  { module: "Limit Approve", slugs: ["credit-limit-approve"] },
  { module: "Sap Company Configuration", slugs: ["view-sap-company-configuration"] },
  { module: "Staff", slugs: ["create-staff", "view-staff", "update-staff", "delete-staff"] },
];

const formatPermissionLabel = (name: string) =>
  (name || "")
    .split("-")
    .map((word) => (word ? word.charAt(0).toUpperCase() + word.slice(1) : word))
    .join("-");

const EditRole = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();

  const [language, setLanguage] = useState("en");
  const { data: permissions } = useSelector((state: any) => state.permissions);
  const { detail } = useSelector((state: any) => state.roles);

  const [formData, setFormData] = useState({
    name: "",
    permissions: [],
  });

  useEffect(() => {
    if (!(permissions && permissions.length > 0)) {
      dispatch(getPermissionData({ language }));
    }
  }, []);

  useEffect(() => {
    if (id) {
      dispatch(getRoleByIdData({ id }));
    }
  }, [id]);

  useEffect(() => {
    if (detail) {
      setFormData((prev) => ({
        ...prev,
        name: detail.name || "",
        permissions: (detail.permissions || [])
          .map((permission: any) => permission?._id)
          .filter(Boolean),
      }));
    }
  }, [detail]);

  const groupedPermissions = useMemo(() => {
    const allPermissions = permissions || [];
    const byName: Record<string, any> = {};
    allPermissions.forEach((permission: any) => {
      byName[permission?.name] = permission;
    });

    const used = new Set<string>();
    const groups = PERMISSION_GROUPS.map((group) => {
      const items = group.slugs
        .map((slug) => byName[slug])
        .filter(Boolean);
      items.forEach((permission: any) => used.add(permission._id));
      return { module: group.module, items };
    }).filter((group) => group.items.length > 0);

    const remaining = allPermissions.filter(
      (permission: any) => !used.has(permission._id)
    );
    if (remaining.length > 0) {
      groups.push({ module: "Other", items: remaining });
    }

    return groups;
  }, [permissions]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    if (name === "permissions") {
      setFormData((prev) => {
        const current = prev.permissions || [];
        const next = checked
          ? [...current, value]
          : current.filter((perm) => perm !== value);
        return { ...prev, permissions: next };
      });
    } else {
      setFormData((prev) => ({
        ...prev,
        [name]: type === "checkbox" ? checked : value,
      }));
    }
  };

  const handleSubmit = () => {
    if (!formData.name) {
      toast.error("Name is required.");
      return;
    }
    dispatch(
      updateRoleData({
        id,
        data: {
          name: formData.name,
          permissions: formData.permissions,
          permission_ids: formData.permissions,
        },
      })
    )
      .then((action) => {
        if (updateRoleData.rejected.match(action)) {
          toast.error(action.error.message || "Failed to update role");
        } else {
          toast.success("Role updated successfully");
          navigate("/roles");
        }
      })
      .catch((err) => {
        toast.error(err?.response?.data?.message || "Failed to update role");
      });
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <SoftBox py={3}>
        <SoftBox mb={3}>
          <SoftTypography variant="h5" fontWeight="bold" textAlign="left">
            Edit Role
          </SoftTypography>
        </SoftBox>

        <Card sx={{ borderRadius: "12px", p: { xs: 2, md: 3 } }}>
          <SoftBox component="form" role="form">
            <SoftBox mb={2} maxWidth="420px">
              <FormControl fullWidth size="small">
                <SoftTypography
                  htmlFor="language"
                  style={{
                    fontSize: "15px",
                    fontWeight: "500",
                    lineHeight: "normal",
                    marginBottom: "10px",
                  }}
                >
                  Language
                </SoftTypography>
                <Select
                  id="language"
                  name="language"
                  value={language}
                  onChange={(e) => setLanguage(e.target.value)}
                  IconComponent={KeyboardArrowDownIcon}
                  fullWidth
                  size="small"
                  sx={{
                    padding: "0px 8px !important",
                    height: "40px !important",
                    display: "block !important",
                    ".MuiSelect-select": {
                      padding: "0px 8px !important",
                      height: "40px !important",
                    },
                    ".MuiSelect-nativeInput": {
                      height: "40px !important",
                    },
                  }}
                >
                  <MenuItem style={{ marginBottom: "5px" }} value="en">
                    English
                  </MenuItem>
                  <MenuItem value="ar">Arabic</MenuItem>
                </Select>
              </FormControl>
            </SoftBox>

            <SoftBox mb={3} maxWidth="420px">
              <SoftBox mb={1} ml={0.5}>
                <SoftTypography component="label" variant="caption" fontWeight="bold">
                  Name
                </SoftTypography>
              </SoftBox>
              <SoftInput
                type="text"
                name="name"
                placeholder="Enter the Role Name"
                value={formData.name}
                onChange={handleChange}
              />
            </SoftBox>

            <SoftBox mb={1}>
              <SoftTypography
                component="label"
                variant="caption"
                fontWeight="bold"
                textTransform="uppercase"
                letterSpacing="0.5px"
              >
                Permissions
              </SoftTypography>
            </SoftBox>

            <TableContainer
              sx={{
                border: "1px solid #e9ecef",
                borderRadius: "10px",
                mt: 1,
              }}
            >
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ backgroundColor: "#f8f9fa" }}>
                    <TableCell
                      sx={{
                        fontWeight: 700,
                        fontSize: "0.72rem",
                        letterSpacing: "0.6px",
                        textTransform: "uppercase",
                        color: "#7b809a",
                        width: "70%",
                      }}
                    >
                      Permission
                    </TableCell>
                    <TableCell
                      align="center"
                      sx={{
                        fontWeight: 700,
                        fontSize: "0.72rem",
                        letterSpacing: "0.6px",
                        textTransform: "uppercase",
                        color: "#7b809a",
                      }}
                    >
                      Actions
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {groupedPermissions.map((group) => (
                    <React.Fragment key={group.module}>
                      <TableRow>
                        <TableCell
                          colSpan={2}
                          sx={{
                            backgroundColor: "#f8f9fa",
                            fontWeight: 700,
                            fontSize: "0.78rem",
                            color: "#344767",
                            borderBottom: "1px solid #e9ecef",
                          }}
                        >
                          {group.module}
                        </TableCell>
                      </TableRow>
                      {group.items.map((permission) => (
                        <TableRow
                          key={permission._id}
                          hover
                          sx={{ "&:last-child td": { borderBottom: "1px solid #e9ecef" } }}
                        >
                          <TableCell
                            sx={{
                              fontSize: "0.82rem",
                              color: "#344767",
                              borderBottom: "1px solid #f1f3f5",
                            }}
                          >
                            {formatPermissionLabel(permission.name)}
                          </TableCell>
                          <TableCell
                            align="center"
                            sx={{ borderBottom: "1px solid #f1f3f5" }}
                          >
                            <Checkbox
                              name="permissions"
                              id={permission.name}
                              value={permission._id}
                              checked={
                                formData.permissions?.includes(permission._id) || false
                              }
                              onChange={handleChange}
                              size="small"
                              sx={{
                                color: "#cbd3da",
                                "&.Mui-checked": { color: "#344767" },
                              }}
                            />
                          </TableCell>
                        </TableRow>
                      ))}
                    </React.Fragment>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>

            <SoftBox mt={4} mb={1} display="flex" justifyContent="flex-end">
              <SoftButton
                variant="gradient"
                color="dark"
                onClick={handleSubmit}
                type="submit"
                sx={{
                  textTransform: "uppercase",
                  borderRadius: "8px",
                  px: 4,
                  py: 1.25,
                  fontSize: "0.8rem",
                  fontWeight: 600,
                  letterSpacing: "1px",
                }}
              >
                Update
              </SoftButton>
            </SoftBox>
          </SoftBox>
        </Card>
      </SoftBox>
    </DashboardLayout>
  );
};

export default EditRole;
